/**
 * Shared Bauhaus primitives. Small and dependency-free so any page can
 * adopt them while it migrates off the old zinc palette.
 */
import React from "react";
import { Link } from "react-router-dom";
import type { Accent, Glyph, Shape } from "./nav";
import {
  TbAtom, TbAtom2, TbFlask, TbChartDots3, TbTools, TbBook, TbSearch, TbLayoutGrid,
  TbCertificate, TbNews, TbCoin, TbCpu, TbGift, TbInfinity, TbBrush, TbStar,
  TbMicroscope, TbTestPipe, TbCompass, TbSchool, TbComponents, TbHexagons,
  TbAugmentedReality, Tb3DCubeSphere, TbDeviceMobileRotated, TbQuestionMark, TbTable, TbScale,
  TbCalculator, TbScan, TbHandClick, TbHandMove, TbRotate360, TbBulb, TbStack2, TbDevices, TbFocusCentered,
} from "react-icons/tb";
import type { IconType } from "react-icons";

/** Line glyphs that replace the old circle/square/triangle marks. */
export const GLYPHS: Record<Glyph, IconType> = {
  atom: TbAtom, orbital: TbAtom2, workbench: TbFlask, density: TbChartDots3,
  tools: TbTools, learn: TbBook, search: TbSearch, start: TbLayoutGrid,
  proof: TbCertificate, articles: TbNews, pricing: TbCoin, hardware: TbCpu,
  shop: TbGift, free: TbStar, plan: TbHexagons, unlimited: TbInfinity,
  playground: TbBrush, microscope: TbMicroscope, lab: TbTestPipe,
  compass: TbCompass, school: TbSchool, components: TbComponents,
  ar: TbAugmentedReality, cube: Tb3DCubeSphere, remote: TbDeviceMobileRotated, quiz: TbQuestionMark,
  table: TbTable, scale: TbScale, calculator: TbCalculator, scan: TbScan, tap: TbHandClick,
  move: TbHandMove, rotate: TbRotate360, bulb: TbBulb, stack: TbStack2, devices: TbDevices,
  focus: TbFocusCentered,
};

const ACCENT_VAR: Record<Accent, string> = {
  red: "var(--ev-red)",
  yellow: "var(--ev-yellow)",
  blue: "var(--ev-blue)",
  brand: "var(--ev-brand, var(--ev-red))",
};

/** Solid geometric mark: the navigation and section cue. Decorative. */
export function Mark({
  shape,
  glyph,
  accent = "red",
  size = 14,
  outline = false,
  className = "",
}: {
  shape?: Shape;
  glyph?: Glyph;
  accent?: Accent;
  size?: number;
  outline?: boolean;
  className?: string;
}) {
  const c = ACCENT_VAR[accent];
  if (glyph) {
    const Icon = GLYPHS[glyph];
    return (
      <Icon
        size={Math.round(size * 1.5)}
        strokeWidth={1.75}
        color={c}
        aria-hidden="true"
        focusable="false"
        className={`shrink-0 ${className}`}
      />
    );
  }
  const fill = outline ? "none" : c;
  const stroke = outline ? c : "none";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className}`}
    >
      {shape === "circle" && <circle cx="10" cy="10" r={outline ? 8.5 : 10} fill={fill} stroke={stroke} strokeWidth="3" />}
      {shape === "square" && <rect x={outline ? 1.5 : 0} y={outline ? 1.5 : 0} width={outline ? 17 : 20} height={outline ? 17 : 20} fill={fill} stroke={stroke} strokeWidth="3" />}
      {shape === "triangle" && <polygon points="10,0 20,20 0,20" fill={fill} stroke={stroke} strokeWidth="3" strokeLinejoin="miter" />}
    </svg>
  );
}

/**
 * Brand mark: the app icon. Defaults to ElectronVisualized (same artwork
 * as the macOS app); `app="atomizer"` shows the Atomizer AR icon for the
 * Atomizer shells (iOS, Mac, Vision Pro).
 */
export function BrandMark({ size = 12, app = "ev" }: { size?: number; app?: "ev" | "atomizer" }) {
  const px = Math.round(size * 1.9);
  const base = app === "atomizer" ? "/brand/atomizer-icon" : "/brand/ev-icon";
  return (
    <img
      src={`${base}-64.png`}
      srcSet={`${base}-64.png 1x, ${base}-128.png 2x`}
      width={px}
      height={px}
      alt=""
      aria-hidden="true"
      className="shrink-0 rounded-[22%]"
    />
  );
}

/** Numbered section header: thick rule, index, mark, title. */
export function SectionHeader({
  index,
  title,
  shape = "square",
  glyph,
  accent = "red",
  action,
  id,
}: {
  index?: string;
  title: string;
  shape?: Shape;
  glyph?: Glyph;
  accent?: Accent;
  action?: React.ReactNode;
  id?: string;
}) {
  return (
    <div className="ev-rule-thick flex flex-wrap items-end justify-between gap-4 pt-5 pb-8">
      <div className="flex items-center gap-3">
        <Mark shape={shape} glyph={glyph} accent={accent} size={16} />
        {index && <span className="ev-eyebrow tabular-nums">{index}</span>}
        <h2 id={id} className="ev-h2">
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}

type BtnVariant = "primary" | "outline" | "ghost";

function btnClass(variant: BtnVariant, size?: "sm") {
  return `ev-btn ev-btn-${variant}${size ? " ev-btn-sm" : ""}`;
}

export function Button({
  variant = "primary",
  size,
  className = "",
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: "sm" }) {
  return <button type="button" className={`${btnClass(variant, size)} ${className}`} {...rest} />;
}

export function ButtonLink({
  to,
  variant = "primary",
  size,
  className = "",
  children,
  onClick,
}: {
  to: string;
  variant?: BtnVariant;
  size?: "sm";
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <Link to={to} className={`${btnClass(variant, size)} ${className}`} onClick={onClick}>
      {children}
    </Link>
  );
}

export function Arrow({ external = false }: { external?: boolean }) {
  return (
    <span className="ev-arrow" aria-hidden="true">
      {external ? "↗" : "→"}
    </span>
  );
}
